/**
 * Config Help registry.
 *
 * Static, typed catalog that maps a config-driven admin surface (a wizard step,
 * a detail card, a list table) to the DEDICATED configuration page that drives
 * it. This is platform documentation about our own config UI - identical for
 * every tenant - so it lives in code, not tenant config. It is deliberately
 * generic: keys name features/surfaces, never tenants, and every route points
 * at a dedicated settings/manage page (never the generic Entity Editor or
 * Workflow Designer).
 *
 * Consumed by <ConfigHelp> (the "?" popover) and <EditConfigLink> (the inline
 * per-card link). Copy uses plain hyphens only, never em/en dashes.
 */

/**
 * Concrete record ids the caller has resolved for the surface on screen. A tip
 * whose exact config location needs one of these (e.g. a specific warranty
 * template's Display tab) builds its route from here. When a required id is
 * missing, the tip resolves to `null` and is dropped, so the "?" button never
 * shows a link that does not lead to the exact configuration.
 */
export interface ConfigHelpParams {
  /** Warranty template (a.k.a. warranty class) uuid - drives the warranty
   * template detail route `/manage/warranties/templates/:uuid`. */
  warrantyTemplateUuid?: string | null
}

/**
 * A tip's route. Either a static path, or a builder that produces the exact
 * path from the resolved ids. A builder returns `null` when a required id is
 * absent, which drops the tip (see `collectTips`).
 */
export type ConfigTipRoute =
  | string
  | ((params: ConfigHelpParams) => string | null)

/** A single "here is where this is configured" tip shown in the "?" popover. */
export interface ConfigTip {
  /** Short topic heading, e.g. "Host fields". */
  title: string
  /** One well-structured sentence explaining what this config controls. */
  paragraph: string
  /** Dedicated config route to open (static, or built from resolved ids). */
  route: ConfigTipRoute
  /** Human label for the link, e.g. "Host Templates". */
  label: string
}

/**
 * A config-driven section (a card, step, or surface). `primaryRoute` /
 * `primaryLabel` back the inline "Edit config" link; `tips` back the "?"
 * popover (usually one, at most a few).
 */
export interface ConfigSection {
  primaryRoute: string
  primaryLabel: string
  tips: ConfigTip[]
}

export type ConfigSectionKey =
  // Registration wizard
  | "reg.steps"
  | "reg.product_type"
  | "reg.host_fields"
  | "reg.early_attributes"
  | "reg.product_fields"
  | "reg.customer_fields"
  | "reg.purchase_fields"
  | "reg.warranty"
  | "reg.custom_fields"
  | "reg.validation"
  | "reg.approval"
  // Claims wizard
  | "claim.type"
  | "claim.holding_lookup"
  | "claim.details"
  | "claim.attachments"
  | "claim.detail_fields"
  // Display / table surfaces
  | "display.reg_table"
  | "display.reg_card"
  | "display.product_card"
  | "display.customer_card"
  | "display.purchase_card"
  | "display.warranty_card"
  | "display.warranty_table"
  | "display.claim_host_card"
  | "display.hoa_table"

const REGISTRATION_SETTINGS = "/settings/registration"
const HOST_TEMPLATES = "/manage/products/host-templates"
const PRODUCT_TEMPLATES = "/manage/products/templates"
const CUSTOMER_SETTINGS = "/manage/products/customers"
const TRANSACTION_TEMPLATES = "/manage/products/transaction-templates"
const WARRANTY_TEMPLATES = "/manage/warranties/templates"
const WARRANTY_MAPPINGS = "/manage/warranties/mappings"
const CLAIM_SCHEMAS = "/manage/claims/schemas"
const CLAIM_REGISTRATION = "/manage/claims/registration"

/**
 * URL-hash suffix that focuses the "Display" tab on the manage template/schema
 * detail panels (Product, Host, Transaction Templates, Customer settings). The
 * pages read it via `useHashTab`, so a card-display tip lands on the exact tab
 * where the fields are configured, not the default General tab.
 */
const DISPLAY_TAB = "#display"

export const CONFIG_SECTIONS: Record<ConfigSectionKey, ConfigSection> = {
  // -- Registration wizard ---------------------------------------------------
  "reg.steps": {
    primaryRoute: REGISTRATION_SETTINGS,
    primaryLabel: "Registration settings",
    tips: [
      {
        title: "Steps and options",
        paragraph:
          "The steps shown here, their order, and options like existing-host selection and certificates are set in Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },
  "reg.product_type": {
    primaryRoute: PRODUCT_TEMPLATES,
    primaryLabel: "Product Templates",
    tips: [
      {
        title: "Product templates",
        paragraph:
          "The templates offered on this step are the product templates flagged registration-selectable in Product Templates.",
        route: PRODUCT_TEMPLATES,
        label: "Product Templates",
      },
    ],
  },
  "reg.host_fields": {
    primaryRoute: HOST_TEMPLATES,
    primaryLabel: "Host Templates",
    tips: [
      {
        title: "Host fields",
        paragraph:
          "The fields captured for the host or property (and their lookup providers) come from the matching host template in Host Templates.",
        route: HOST_TEMPLATES,
        label: "Host Templates",
      },
    ],
  },
  "reg.early_attributes": {
    primaryRoute: REGISTRATION_SETTINGS,
    primaryLabel: "Early registration attributes",
    tips: [
      {
        title: "Early attributes",
        paragraph:
          "Which host attributes are captured early in the flow is set in the Early Registration Attributes section of Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },
  "reg.product_fields": {
    primaryRoute: PRODUCT_TEMPLATES,
    primaryLabel: "Product Templates",
    tips: [
      {
        title: "Product fields",
        paragraph:
          "The additional product details captured here are the product template attributes defined in Product Templates.",
        route: PRODUCT_TEMPLATES,
        label: "Product Templates",
      },
    ],
  },
  "reg.customer_fields": {
    primaryRoute: CUSTOMER_SETTINGS,
    primaryLabel: "Customer fields",
    tips: [
      {
        title: "Customer fields",
        paragraph:
          "The customer fields and their schema are defined on the Customer settings page.",
        route: CUSTOMER_SETTINGS,
        label: "Customer fields",
      },
      {
        title: "Required or optional",
        paragraph:
          "Whether each customer field is required, optional, or hidden is set per step in Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },
  "reg.purchase_fields": {
    primaryRoute: TRANSACTION_TEMPLATES,
    primaryLabel: "Transaction Templates",
    tips: [
      {
        title: "Purchase fields",
        paragraph:
          "The purchase fields collected here are the transaction template attributes defined in Transaction Templates.",
        route: TRANSACTION_TEMPLATES,
        label: "Transaction Templates",
      },
      {
        title: "Required or optional",
        paragraph:
          "Whether each purchase field is required, optional, or hidden is set per step in Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },
  "reg.warranty": {
    primaryRoute: WARRANTY_TEMPLATES,
    primaryLabel: "Warranty Templates",
    tips: [
      {
        title: "Warranty options",
        paragraph:
          "The warranties offered here, their coverage fields, and the availability rules that filter them live under each template in Warranty Templates.",
        route: WARRANTY_TEMPLATES,
        label: "Warranty Templates",
      },
    ],
  },
  "reg.custom_fields": {
    primaryRoute: REGISTRATION_SETTINGS,
    primaryLabel: "Registration fields",
    tips: [
      {
        title: "Custom fields",
        paragraph:
          "Your own custom registration fields, and the step each appears on, are managed in the Registration Fields section of Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },
  "reg.validation": {
    primaryRoute: REGISTRATION_SETTINGS,
    primaryLabel: "Validation rules",
    tips: [
      {
        title: "Validation rules",
        paragraph:
          "The checks that block or warn during registration are configured in the Validation Rules section of Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },
  "reg.approval": {
    primaryRoute: REGISTRATION_SETTINGS,
    primaryLabel: "Approval rules",
    tips: [
      {
        title: "Approval rules",
        paragraph:
          "The conditions that require a registration to be approved are set in the Approval Rules section of Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
    ],
  },

  // -- Claims wizard ---------------------------------------------------------
  "claim.type": {
    primaryRoute: CLAIM_SCHEMAS,
    primaryLabel: "Claim Schemas",
    tips: [
      {
        title: "Claim types",
        paragraph:
          "The claim types offered here are the options on the claim_type attribute of your claim schemas in Claim Schemas.",
        route: CLAIM_SCHEMAS,
        label: "Claim Schemas",
      },
    ],
  },
  "claim.holding_lookup": {
    primaryRoute: CLAIM_SCHEMAS,
    primaryLabel: "Claim Schemas",
    tips: [
      {
        title: "Registration lookup",
        paragraph:
          "Whether a claim type needs a linked registration is set by the requires-holding option on that claim type in Claim Schemas.",
        route: CLAIM_SCHEMAS,
        label: "Claim Schemas",
      },
    ],
  },
  "claim.details": {
    primaryRoute: CLAIM_REGISTRATION,
    primaryLabel: "Claim capture fields",
    tips: [
      {
        title: "Captured fields",
        paragraph:
          "Which claim fields are captured, their order, and their labels for admin and consumer creation are curated on the Claims Configuration, Registration page.",
        route: CLAIM_REGISTRATION,
        label: "Claim capture fields",
      },
      {
        title: "Field definitions",
        paragraph:
          "The underlying claim fields and their types are defined on your claim schema in Claim Schemas.",
        route: CLAIM_SCHEMAS,
        label: "Claim Schemas",
      },
    ],
  },
  "claim.attachments": {
    primaryRoute: CLAIM_REGISTRATION,
    primaryLabel: "Claim capture fields",
    tips: [
      {
        title: "Photo and file fields",
        paragraph:
          "File, photo, and document fields are ordinary claim fields; add or reorder them on the Claims Configuration, Registration page.",
        route: CLAIM_REGISTRATION,
        label: "Claim capture fields",
      },
    ],
  },
  "claim.detail_fields": {
    primaryRoute: CLAIM_REGISTRATION,
    primaryLabel: "Claim fields",
    tips: [
      {
        title: "Claim fields",
        paragraph:
          "The claim fields shown on this page are curated on the Claims Configuration, Registration page.",
        route: CLAIM_REGISTRATION,
        label: "Claim fields",
      },
    ],
  },

  // -- Display / table surfaces (dedicated Display tabs) ---------------------
  "display.reg_table": {
    primaryRoute: HOST_TEMPLATES,
    primaryLabel: "Table columns",
    tips: [
      {
        title: "Table columns",
        paragraph:
          "The columns in this table are set in the Display tab of Host Templates (or Product Templates for product-led tenants).",
        route: `${HOST_TEMPLATES}${DISPLAY_TAB}`,
        label: "Host Templates, Display",
      },
    ],
  },
  "display.reg_card": {
    primaryRoute: HOST_TEMPLATES,
    primaryLabel: "Card display",
    tips: [
      {
        title: "Card fields",
        paragraph:
          "The fields shown on the registration cards are set in the Display tab of Host Templates, Product Templates, or Customer settings.",
        route: `${HOST_TEMPLATES}${DISPLAY_TAB}`,
        label: "Host Templates, Display",
      },
    ],
  },
  "display.product_card": {
    primaryRoute: PRODUCT_TEMPLATES,
    primaryLabel: "Card display",
    tips: [
      {
        title: "Product card",
        paragraph:
          "The fields shown on the product card are set in the Display tab of Product Templates.",
        route: `${PRODUCT_TEMPLATES}${DISPLAY_TAB}`,
        label: "Product Templates, Display",
      },
    ],
  },
  "display.customer_card": {
    primaryRoute: CUSTOMER_SETTINGS,
    primaryLabel: "Card display",
    tips: [
      {
        title: "Customer card",
        paragraph:
          "The fields shown on the customer card are set in the Display tab of Customer settings.",
        route: `${CUSTOMER_SETTINGS}${DISPLAY_TAB}`,
        label: "Customer settings, Display",
      },
    ],
  },
  "display.purchase_card": {
    primaryRoute: TRANSACTION_TEMPLATES,
    primaryLabel: "Card display",
    tips: [
      {
        title: "Purchase card",
        paragraph:
          "The fields shown on the purchase card are set in the Display tab of Transaction Templates.",
        route: `${TRANSACTION_TEMPLATES}${DISPLAY_TAB}`,
        label: "Transaction Templates, Display",
      },
    ],
  },
  "display.warranty_card": {
    primaryRoute: WARRANTY_TEMPLATES,
    primaryLabel: "Card display",
    tips: [
      {
        title: "Warranty card",
        paragraph:
          "The fields shown on this warranty card are set in the Display tab of this warranty template.",
        // Deep-links to the exact template's Display tab. Requires the resolved
        // warranty template uuid; without it the tip is dropped rather than
        // linking to the generic template list.
        route: (p) =>
          p.warrantyTemplateUuid
            ? `${WARRANTY_TEMPLATES}/${p.warrantyTemplateUuid}/display`
            : null,
        label: "Warranty Template, Display",
      },
    ],
  },
  "display.claim_host_card": {
    primaryRoute: HOST_TEMPLATES,
    primaryLabel: "Card display",
    tips: [
      {
        title: "Host card",
        paragraph:
          "The host card shown on a claim is driven by the host template; set its fields in the Display tab of Host Templates.",
        route: `${HOST_TEMPLATES}${DISPLAY_TAB}`,
        label: "Host Templates, Display",
      },
    ],
  },
  "display.warranty_table": {
    primaryRoute: WARRANTY_TEMPLATES,
    primaryLabel: "Warranty Templates",
    tips: [
      {
        title: "Warranty templates",
        paragraph:
          "Each template's coverage fields, availability rules, and card display are edited inside that template in Warranty Templates.",
        route: WARRANTY_TEMPLATES,
        label: "Warranty Templates",
      },
      {
        title: "Product mappings",
        paragraph:
          "Which products resolve to which warranty at registration is set in Warranty mappings.",
        route: WARRANTY_MAPPINGS,
        label: "Warranty mappings",
      },
    ],
  },
  "display.hoa_table": {
    primaryRoute: REGISTRATION_SETTINGS,
    primaryLabel: "Registration settings",
    tips: [
      {
        title: "Registration and grouping",
        paragraph:
          "How homes are registered and grouped into communities is set in Settings, Registration.",
        route: REGISTRATION_SETTINGS,
        label: "Registration settings",
      },
      {
        title: "Property fields",
        paragraph:
          "The property fields captured for each home come from the matching host template in Host Templates.",
        route: HOST_TEMPLATES,
        label: "Host Templates",
      },
    ],
  },
}

/** A tip whose route has been resolved to a concrete, navigable path. */
export interface ResolvedConfigTip extends Omit<ConfigTip, "route"> {
  route: string
}

/**
 * Flatten the tips for a set of section keys into concrete, navigable tips,
 * de-duplicated by resolved route (so the same config page is not listed twice
 * when several sections point at it).
 *
 * `params` carries the resolved record ids for the surface on screen. A tip
 * whose route is a builder that returns `null` (a required id is missing) is
 * dropped - the "?" popover only ever shows links that lead to the exact
 * configuration location.
 */
export function collectTips(
  keys: ConfigSectionKey[],
  params: ConfigHelpParams = {},
): ResolvedConfigTip[] {
  const seen = new Set<string>()
  const out: ResolvedConfigTip[] = []
  for (const key of keys) {
    const section = CONFIG_SECTIONS[key]
    if (!section) continue
    for (const tip of section.tips) {
      const route =
        typeof tip.route === "function" ? tip.route(params) : tip.route
      if (!route || seen.has(route)) continue
      seen.add(route)
      out.push({ ...tip, route })
    }
  }
  return out
}
